import moment from 'moment';
import { Address, ConfiguredFields, ConfiguredFieldTypes, IColumn } from '../store/modules/application/types';
import { PersonName } from '../store/modules/person/types';
import { camelCase, isEmpty, isEqual, isObject } from 'lodash';
import jsonpath from 'jsonpath';
import { EXTRACT_COLUMN_FIELDS_REGEX } from 'src/store/app.constants';

export function doubleEncodeURL(url: string) {
    return encodeURIComponent(encodeURIComponent(url));
}

export function doubleDecodeURL(url: string) {
    return decodeURIComponent(decodeURIComponent(url));
}

export function displayCurrencyAmount(amount: number) {
    return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export function getPersonSimpleName(name: PersonName): string {
    if (!name) {
        return '(no name)';
    }

    if (typeof name === 'string') {
        return name;
    }

    return [name.firstName, name.middleName, name.lastName, name.nameSuffix].filter((x) => x).join(' ');
}

export const toUtcStringConversion = (timestamp: any) => {
    const parsedToDate = new Date(timestamp);
    const utcTimeStamp = moment(parsedToDate.toUTCString()).format('YYYY/MM/DD');
    return utcTimeStamp;
};

export const removeEmptyObjects: any = (object: object) => {
    return Object.fromEntries(
        Object.entries(object).flatMap(([key, value]) =>
            typeof value !== 'object'
                ? [[key, value]]
                : ((value = removeEmptyObjects(value)), Object.keys(value).length > 0 ? [[key, value]] : []),
        ),
    );
};

export function stripNulls(value: any): void {
    for (const key in value) {
        if (value[key] === null) {
            delete value[key];
        } else if (typeof value[key] === 'object') {
            stripNulls(value[key]);
        }
    }
}

export function getFullAddress(address: Address | undefined): string {
    let commaSeparatedFields = [];
    let spaceSeparatedFields = [];

    if (isObject(address)) {
        for (const [key, value] of Object.entries(address!)) {
            if (key && value) {
                if (key === 'line1' || key === 'line2' || key === 'city' || key === 'state') {
                    commaSeparatedFields.push(value);
                } else {
                    spaceSeparatedFields.push(value);
                }
            }
        }
    }

    return commaSeparatedFields.join(', ') + ' ' + spaceSeparatedFields.join(' ');
}
export const getColumnFormat = (dataType: string | undefined) => {
    return dataType ?? 'text';
};

// TODO: Need to use this function everywhere when configuring columns of tables in future tickets.
export const getFormattedColumns = (configuredColumns: ConfiguredFields) => {
    const formattedColumnList = configuredColumns?.fields?.map((field: ConfiguredFieldTypes) => {
        return {
            id: `${camelCase(field.name)}|${getColumnFormat(field.dataType)}`,
            label: field.name,
            // Need to slice first two characters '$.' in order to match column data field
            field: field.dataType === 'template' ? field.data : field.data.slice(2),
            format: field.format,
            sortFields: field?.sortFields,
        };
    });
    return formattedColumnList?.length ? formattedColumnList : [];
};

export const extractValuesFromRegex = (values: string) => {
    return values.match(EXTRACT_COLUMN_FIELDS_REGEX) || [];
};

export const getFlatColumnFieldsFromList = (data: any, columns: IColumn[]) => {
    let configuredColumnsList: any = [];
    data.flatMap((obj: any) => {
        let resultedObj: any = {};
        columns.map((column: IColumn) => {
            let computedValue = null;
            const columnDataType = column.id.split('|')[1];
            const fields = extractValuesFromRegex(column.field);

            switch (columnDataType) {
                case 'template':
                    computedValue = fields.flatMap((targetPath) => {
                        return targetPath.split('.').reduce((resultObj, value) => {
                            const fieldValue = value.split('!');
                            if (fieldValue.length > 1 && resultObj) {
                                switch (fieldValue[1]) {
                                    //More cases will be added here in the future.
                                    case 'date':
                                        return resultObj[fieldValue[0]]
                                            ? moment(resultObj[fieldValue[0]]).format('L')
                                            : '';
                                    case 'time':
                                        return resultObj[fieldValue[0]]
                                            ? moment(resultObj[fieldValue[0]]).format('LT')
                                            : '';
                                    case 'day':
                                        return resultObj[fieldValue[0]]
                                            ? moment(resultObj[fieldValue[0]]).format('dddd')
                                            : '';

                                    default:
                                        return resultObj && resultObj[value];
                                }
                            } else return resultObj && resultObj[value];
                        }, obj);
                    });
                    break;
                case 'dateTimeRange':
                case 'dateTimeRangeDay':
                    // This check was added specifically to cater the case where we need to show day name / day time range.
                    computedValue = fields.flatMap((targetPath) =>
                        targetPath.split('.').reduce((resultObj, value) => resultObj && resultObj[value], obj),
                    )[0];
                    break;
                default:
                    computedValue = jsonpath.query(obj, column.field).toString().split(',').join(', ');
                    break;
            }
            return {
                [column.field]: resultedObj = {
                    ...resultedObj,
                    [column.id.split('|')[0]]: computedValue,
                },
            };
        });
        configuredColumnsList.push({ ...resultedObj, id: obj.id });
    });
    return configuredColumnsList;
};

export const getFormattedField = (field: string) => {
    let counter = 0;
    // This is to format the string in the same
    // format as 'dataType' of the columns configuration
    // {abc} {def} - {ghi} would be formatted as {0} {1} - {2} ...

    const formattedString = field.replace(/\{[\s\S]*?\}/g, () => {
        return ` {${counter++}} `;
    });
    return formattedString.toString().split(',').join('');
};

// Returns an object containing only properties with changes in value
// Arrays are treated as absolute values, and its nested children won't be compared individually
export const getObjectChanges = (oldObject: any, newObject: any) => {
    let differences: any;
    if (typeof newObject !== 'object' || newObject === null || oldObject === null || oldObject === undefined) {
        return newObject;
    }
    if (Array.isArray(newObject)) {
        if (isEqual(newObject, oldObject)) return undefined;
        return newObject;
    }
    differences = {};
    for (const i in newObject) {
        if (typeof newObject[i] === 'object') {
            differences[i] = getObjectChanges(oldObject[i], newObject[i]);
            if (!differences[i]) {
                delete differences[i];
            }
        } else {
            if (oldObject[i] !== newObject[i] && newObject[i] !== undefined) {
                differences[i] = newObject[i];
            }
        }
    }
    return isEmpty(differences) ? undefined : differences;
};
